import ContentWrapper from 'components/ContentWrapper'
import { Typography } from 'components/common'
import { useCollectiveContext } from 'context/CollectiveContext'
import {
  DiscussionBoardQueryResult,
  DiscussionBoardQueryVariables,
  useCategoryAddedSubscription,
  useDiscussionBoardChangedSubscription,
  useDiscussionBoardQuery
} from 'graphql/generated'
import React, { createContext, useContext, PropsWithChildren } from 'react'
import CollectivePageHeader from '../Common/CollectivePageHeader/CollectivePageHeader'
import Spinner from 'components/Spinner'
import TokenGate from '../MembershipGate/TokenGate'
import MembershipGate from '../MembershipGate/MembershipGate'

interface ForumContextProps {
  selectedCategoryId?: string
}

interface ForumContextShape {
  selectedCategoryId: string
  selectedCategory: DiscussionBoardQueryResult['discussionBoard']['categories'][0]
  categories: DiscussionBoardQueryResult['discussionBoard']['categories']
  discussionBoard: DiscussionBoardQueryResult['discussionBoard']
  refetchBoard: CallbackWithOptionalParam<DiscussionBoardQueryVariables>
}

const ForumContext = createContext<ForumContextShape>({} as ForumContextShape)

export const useForumContext = () => useContext(ForumContext)

export function ForumProvider({
  selectedCategoryId,
  children
}: PropsWithChildren<ForumContextProps>) {
  const { collective } = useCollectiveContext()
  const {
    data,
    loading,
    refetch: refetchBoard
  } = useDiscussionBoardQuery({
    variables: {
      discussionBoardId: collective.discussionBoard.id
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'network-only'
  })

  useDiscussionBoardChangedSubscription({
    variables: {
      discussionBoardId: collective.discussionBoard.id
    },
    onData: () => {
      refetchBoard()
    }
  })

  useCategoryAddedSubscription({
    variables: {
      discussionBoardId: collective.discussionBoard.id
    },
    onData: () => {
      refetchBoard()
    }
  })

  const categories = data?.discussionBoard.categories

  if (!collective) {
    return (
      <ContentWrapper>
        <div className="h-screen-minus-navbar flex flex-col">
          <CollectivePageHeader collective={collective} />
          <Typography>Collective not found</Typography>
        </div>
      </ContentWrapper>
    )
  }

  if (!data?.discussionBoard || !categories) {
    return (
      <ContentWrapper>
        <div className="h-screen-minus-navbar flex flex-col">
          <CollectivePageHeader collective={collective} />
          <div className="flex-1 flex justify-center items-center">
            {loading ? (
              <Spinner />
            ) : (
              <Typography>Forum data not found</Typography>
            )}
          </div>
        </div>
      </ContentWrapper>
    )
  }

  const categoriesFiltered = categories.filter(category =>
    collective.membership?.privileged ? category : !category.archived
  )
  const categoryId = selectedCategoryId || categoriesFiltered[0].id
  const category =
    categoriesFiltered.find(category => category.id === categoryId) ||
    categories[0]

  return (
    <ForumContext.Provider
      value={{
        selectedCategoryId: categoryId,
        selectedCategory: category,
        categories: categoriesFiltered,
        discussionBoard: data.discussionBoard,
        refetchBoard
      }}
    >
      <MembershipGate
        header="Forum is Hidden"
        subheader="The forum is only available to members of the DAO. To join the
    conversation, become a member today!"
      >
        <TokenGate
          header="Forum is Hidden"
          subheader="The forum is only available to token holders. To join the
          conversation, acquire DAO tokens today!"
        >
          {children}
        </TokenGate>
      </MembershipGate>
    </ForumContext.Provider>
  )
}
